/* eslint-disable no-plusplus */

/**
 * Converts a 24-hour format hour to 12-hour format
 * @param {number} hour24 - Hour in 24-hour format (0-23)
 * @returns {number} Hour in 12-hour format (1-12)
 */
const to12Hour = (hour24) => {
  if (hour24 === 0) return 12;
  if (hour24 > 12) return hour24 - 12;
  return hour24;
};

/**
 * Generates an array of time options in 15-minute intervals for a full 24-hour day
 * @returns {Array<{label: string, value: {format_12hr: string, format_24hr: string}}>}
 * Array of time options where:
 * - label: Time in 12-hour format (e.g., "9:00am")
 * - value.format_12hr: Time in 12-hour format (e.g., "9:00am")
 * - value.format_24hr: Time in 24-hour format (e.g., "09:00")
 */
export const getTimeOptions = () => {
  const options = [];

  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const period = h < 12 ? 'am' : 'pm';
      const minutes = m.toString().padStart(2, '0');

      const format12hr = `${to12Hour(h)}:${minutes}${period}`;
      const format24hr = `${h.toString().padStart(2, '0')}:${minutes}`;

      options.push({
        label: format12hr,
        value: {
          format_12hr: format12hr,
          format_24hr: format24hr,
        },
      });
    }
  }

  return options;
};

/**
 * Parses a time string and returns a formatted time object
 * Accepts both 12-hour format (e.g., "9:30am", "11:45pm") and 24-hour format (e.g., "09:30", "23:45")
 * @param {string} timeString - Time string in either 12-hour (with am/pm) or 24-hour format
 * @returns {{label: string, value: {format_12hr: string, format_24hr: string}} | null}
 * Object containing:
 * - label: Time in 12-hour format (e.g., "9:30am")
 * - value.format_12hr: Time in 12-hour format (e.g., "9:30am")
 * - value.format_24hr: Time in 24-hour format (e.g., "09:30")
 * Returns undefined if the time string is invalid
 */
export const getTimeValue = (timeString) => {
  const is12Hour = /am|pm/i.test(timeString);
  let hour24;
  let minutes;

  if (is12Hour) {
    const match = timeString.match(/^(\d{1,2}):(\d{2})(am|pm)$/i);
    if (!match) return undefined;

    const [, hourStr, mins, period] = match;
    const hour12 = parseInt(hourStr, 10);
    minutes = mins;

    if (period.toLowerCase() === 'am') {
      hour24 = hour12 === 12 ? 0 : hour12;
    } else {
      hour24 = hour12 === 12 ? 12 : hour12 + 12;
    }
  } else {
    const match = timeString.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return undefined;

    const [, hourStr, mins] = match;
    hour24 = parseInt(hourStr, 10);
    minutes = mins;
  }

  const hour12 = to12Hour(hour24);
  const period = hour24 < 12 ? 'am' : 'pm';

  const format12hr = `${hour12}:${minutes}${period}`;
  const format24hr = `${hour24.toString().padStart(2, '0')}:${minutes}`;

  return {
    label: format12hr,
    value: {
      format_12hr: format12hr,
      format_24hr: format24hr,
    },
  };
};
